import { useEffect, useState } from "react";
import { api } from "@/api";

/**
 * Tiny inline SVG sparkline. No axes, no labels — just a line on a transparent
 * background. Used in the corner of each dashboard sensor tile to show the
 * trend over the last hour at a glance.
 */
export function Sparkline({ sensorId, width = 120, height = 36, className = "" }) {
  const [points, setPoints] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await api.get(`/sensors/${sensorId}/readings?range=1h`);
        if (!cancelled) setPoints(data?.points || []);
      } catch {
        if (!cancelled) setPoints([]);
      }
    }
    load();
    const t = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [sensorId]);

  if (points.length < 2) {
    return (
      <svg
        width={width}
        height={height}
        className={className}
        data-testid={`sparkline-${sensorId}`}
        aria-hidden
      />
    );
  }

  const values = points.map((p) => p.temp_f);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(0.5, max - min);
  const stepX = width / (points.length - 1);
  const d = points
    .map((p, i) => {
      const x = i * stepX;
      const y = height - ((p.temp_f - min) / span) * height;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(" ");

  return (
    <svg
      width={width}
      height={height}
      className={className}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      data-testid={`sparkline-${sensorId}`}
      aria-hidden
    >
      <path
        d={d}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.7"
      />
    </svg>
  );
}
